import { createContext, ReactNode, useEffect, useMemo } from "react";
import { useMedplumContext } from "@medplum/react-hooks";
import { captureException } from "@sentry/react";

import { medplumClient } from "@/lib/medplum";
import { resolveSessionIds } from "@/lib/utils/helpers";
import { isNullable } from "@/lib/utils/typeof";
import { useProject } from "@/context/project-provider";

export type MedplumSessionState = {
  logout: () => void;
  projectId: string | null;
  practitionerId: string | null;
  isContextLoading: boolean;
  isContextReady: boolean;
};

export const MedplumSessionContext = createContext<MedplumSessionState | null>(
  null
);

type Props = {
  children: ReactNode;
};

export const MedplumSessionProvider: React.FC<Props> = ({ children }) => {
  const { profile, loading } = useMedplumContext();
  const { project } = useProject();

  const logout = () => {
    medplumClient.signOut().catch(captureException);
  };

  const value = useMemo<MedplumSessionState>(
    () => ({
      logout,
      ...resolveSessionIds({
        profile,
        contextProjectId: project?.id,
        clientProjectId: medplumClient.getProject()?.id,
      }),
      isContextLoading: loading,
      isContextReady: !isNullable(profile),
    }),
    [profile, loading, project?.id]
  );

  const { projectId, practitionerId, isContextReady } = value;

  useEffect(() => {
    if (isContextReady && (!projectId || !practitionerId)) {
      captureException(`Missing Medplum context for authenticated user`);
    }
  }, [isContextReady, projectId, practitionerId]);

  return (
    <MedplumSessionContext.Provider value={value}>
      {children}
    </MedplumSessionContext.Provider>
  );
};
